import { createFileRoute, Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';

export const Route = createFileRoute('/welcome')({
  component: WelcomePage,
});

const features = [
  {
    title: 'Dark camera HUD',
    body: 'Full-screen viewfinder, grid, focus reticle, and AI hints that suggest without blocking the frame.',
  },
  {
    title: 'Offline-first sync',
    body: 'SQLite (OPFS) + a durable sync queue. Drafts persist; mutations replay when the radio comes back, with retry and an attempt ceiling.',
  },
  {
    title: 'Timeline + traceable forms',
    body: 'Every capture becomes a timeline event and auto-stitches into the inspection form with source links.',
  },
];

function WelcomePage() {
  return (
    <section className="mx-auto max-w-5xl px-6 py-16">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
      >
        <span className="border-brand/30 bg-brand-soft text-brand-ink inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider">
          <span className="bg-brand h-1.5 w-1.5 rounded-full" /> Variant D · Hi-Fi
        </span>
        <h1 className="mt-5 font-sans text-4xl font-extrabold tracking-[-0.03em] sm:text-5xl">
          Field capture, built for the worst network you'll ever see.
        </h1>
        <p className="text-text-dim mt-4 max-w-2xl text-base sm:text-lg">
          Kapture AI is an inspection tool for the Sarah Chens of the world — the inspector under a
          bridge, mid-tunnel, in a tunnel of a tunnel, without signal. Photos, voice, GPS, barcode.
          Everything stitched into a signed report when you surface.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/design-showcase"
            className="bg-brand text-brand-ink inline-flex h-11 items-center gap-2 rounded-lg px-5 text-sm font-semibold shadow-[0_1px_2px_rgba(255,90,31,0.2)] transition-transform hover:-translate-y-px"
          >
            Open design showcase
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Link>
          <Link
            to="/"
            className="border-stroke bg-surface text-ink hover:bg-surface-2 inline-flex h-11 items-center gap-2 rounded-lg border px-5 text-sm font-semibold transition-colors"
          >
            Open session hub
          </Link>
        </div>
      </motion.div>

      <div className="mt-16 grid gap-5 sm:grid-cols-3">
        {features.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + i * 0.08, duration: 0.4, ease: 'easeOut' }}
            className="rounded-panel border-stroke bg-surface shadow-kap-1 border p-5"
          >
            <div className="text-text-mute font-mono text-[10px] font-semibold uppercase tracking-wider">
              0{i + 1}
            </div>
            <h3 className="mt-2 font-sans text-base font-bold tracking-tight">{f.title}</h3>
            <p className="text-text-dim mt-2 text-sm leading-relaxed">{f.body}</p>
          </motion.div>
        ))}
      </div>

      <div className="rounded-panel border-stroke bg-ink mt-16 border p-6 text-white">
        <div className="font-mono text-[10px] font-semibold uppercase tracking-wider text-white/60">
          stack
        </div>
        <div className="mt-3 flex flex-wrap gap-2 font-mono text-[11px] text-white/80">
          {[
            'Vite',
            'React 18',
            'TypeScript',
            'TanStack Router',
            'Vite PWA + durable SQLite sync queue',
            'SQLite WASM (OPFS)',
            'OPFS media',
            'TanStack Query',
            'Zustand',
            'React Hook Form + Zod',
            'Tailwind + shadcn/ui',
            'Framer Motion',
          ].map((s) => (
            <span key={s} className="rounded-md border border-white/10 bg-white/5 px-2 py-1">
              {s}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
